import { create } from 'zustand';

type DrawerTab = 'jobs' | 'subagents' | 'workflow';

interface TraceDrawerState {
  isOpen: boolean;
  activeTab: DrawerTab;
  /**
   * True after `open()` (no explicit tab) so TraceDrawer's reconcile effect
   * knows to pick the default tab on this open. `openTab()` / `setActiveTab()`
   * clear it so an explicit tab choice is respected. Internal coordination
   * flag — not read by anything outside TraceDrawer.
   */
  pendingDefaultTab: boolean;
  open: () => void;
  openTab: (tab: DrawerTab) => void;
  close: () => void;
  toggle: () => void;
  setActiveTab: (tab: DrawerTab) => void;
  clearPendingDefaultTab: () => void;
}

/**
 * Zustand store for trace drawer visibility state.
 * Separate from URL state to avoid polluting navigation history
 * for transient UI state.
 *
 * The trace ID is intentionally NOT stored here — it is derived from
 * the currently-selected conversation and passed to TraceDrawer as a
 * prop, so switching conversations can never leave stale data in view.
 *
 * Default-tab selection (Jobs while live → Workflow when a trace exists →
 * first available) is owned by the reconcile effect in TraceDrawer, which has
 * the live `hasActiveJobs` / `traceId` context the store cannot see. `open()`
 * requests that default via `pendingDefaultTab`; `openTab(tab)` is the explicit
 * escape hatch (e.g. the HeaderBar "Subagents" button) and bypasses it.
 */
export const useTraceDrawerStore = create<TraceDrawerState>((set, get) => ({
  isOpen: false,
  activeTab: 'workflow',
  pendingDefaultTab: false,
  open: () => set({ isOpen: true, pendingDefaultTab: true }),
  openTab: (tab: DrawerTab) =>
    set({ isOpen: true, activeTab: tab, pendingDefaultTab: false }),
  close: () => set({ isOpen: false, pendingDefaultTab: false }),
  toggle: () => {
    const { isOpen } = get();
    if (isOpen) {
      set({ isOpen: false, pendingDefaultTab: false });
    }
  },
  setActiveTab: (tab: DrawerTab) =>
    set({ activeTab: tab, pendingDefaultTab: false }),
  clearPendingDefaultTab: () => set({ pendingDefaultTab: false }),
}));
